import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { OperatorAuthProvider } from "./operator/operatorAuth";
import { RequireOperator } from "./operator/RequireOperator";
import { OperatorShell } from "./operator/OperatorShell";
import { OperatorLoginScreen } from "@/features/operator/OperatorLoginScreen";
import { OperatorOverviewScreen } from "@/features/operator/OperatorOverviewScreen";
import { OperatorOrgsScreen } from "@/features/operator/OperatorOrgsScreen";
import { OperatorOrgDetailScreen } from "@/features/operator/OperatorOrgDetailScreen";
import { OperatorMailScreen } from "@/features/operator/OperatorMailScreen";
import { OperatorAuditScreen } from "@/features/operator/OperatorAuditScreen";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { VerifyScreen } from "@/features/auth/VerifyScreen";
import { AcceptInviteScreen } from "@/features/auth/AcceptInviteScreen";
import { ForgotPasswordScreen } from "@/features/auth/ForgotPasswordScreen";
import { ResetPasswordScreen } from "@/features/auth/ResetPasswordScreen";
import { SocialCallbackScreen } from "@/features/auth/SocialCallbackScreen";
import { RegisterScreen } from "@/features/onboarding/RegisterScreen";
import { WelcomeScreen } from "@/features/onboarding/WelcomeScreen";
import { DashboardScreen } from "@/features/dashboard/DashboardScreen";
import { ChannelsScreen } from "@/features/channels/ChannelsScreen";
import { IntegrationsScreen } from "@/features/integrations/IntegrationsScreen";
import { MailScreen } from "@/features/mail/MailScreen";
import { TeamScreen } from "@/features/team/TeamScreen";
import { BillingScreen } from "@/features/billing/BillingScreen";
import { BillingReturnScreen } from "@/features/billing/BillingReturnScreen";
import { AuditScreen } from "@/features/audit/AuditScreen";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import { RequireAuth } from "./RequireAuth";
import { AppShell } from "./AppShell";
import { RouteError } from "./ErrorBoundary";

// Public onboarding/auth routes + the authenticated app nested under the AppShell
// layout route. A single pathless root carries `errorElement` so a render/loader
// error in ANY route shows the branded fallback (not React Router's bare default).
export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <Navigate to="/register" replace /> },
      { path: "/register", element: <RegisterScreen /> },
      { path: "/welcome", element: <WelcomeScreen /> },
      { path: "/login", element: <LoginScreen /> },
      { path: "/forgot-password", element: <ForgotPasswordScreen /> },
      { path: "/auth/verify", element: <VerifyScreen /> },
      { path: "/auth/reset-password", element: <ResetPasswordScreen /> },
      { path: "/auth/accept-invite", element: <AcceptInviteScreen /> },
      { path: "/auth/social", element: <SocialCallbackScreen /> },
      { path: "/billing/success", element: <BillingReturnScreen mode="success" /> },
      { path: "/billing/cancel", element: <BillingReturnScreen mode="cancel" /> },
      {
        path: "/app",
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <DashboardScreen /> },
          { path: "channels", element: <ChannelsScreen /> },
          { path: "integrations", element: <IntegrationsScreen /> },
          { path: "mail", element: <MailScreen /> },
          { path: "team", element: <TeamScreen /> },
          { path: "billing", element: <BillingScreen /> },
          { path: "audit", element: <AuditScreen /> },
          { path: "settings", element: <SettingsScreen /> },
        ],
      },
      // Operator console — a SEPARATE realm (operator JWT, own auth context). The
      // OperatorAuthProvider wraps the whole /operator subtree so login + app share it.
      {
        path: "/operator",
        element: (
          <OperatorAuthProvider>
            <Outlet />
          </OperatorAuthProvider>
        ),
        children: [
          { index: true, element: <Navigate to="/operator/app" replace /> },
          { path: "login", element: <OperatorLoginScreen /> },
          {
            path: "app",
            element: (
              <RequireOperator>
                <OperatorShell />
              </RequireOperator>
            ),
            children: [
              { index: true, element: <OperatorOverviewScreen /> },
              { path: "orgs", element: <OperatorOrgsScreen /> },
              { path: "orgs/:id", element: <OperatorOrgDetailScreen /> },
              { path: "mail", element: <OperatorMailScreen /> },
              { path: "audit", element: <OperatorAuditScreen /> },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/register" replace /> },
    ],
  },
]);
